import { useRef, useState } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import { Progress } from "./ui/progress";
import { Download, Upload, FileSpreadsheet, AlertCircle, CheckCircle } from "lucide-react";
import { notify } from "@/lib/utils/toast";
import * as XLSX from "xlsx";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver'; // Nên dùng thư viện này để hỗ trợ tải file trên trình duyệt
import { languageLevelOptions, majorListForScholarship } from "./validations/shared";
import { scholarshipTitleApi } from "@/lib/api/scholarship-title";


export type ImportMode = "onlyAdd" | "addAndCreate";

interface ImportExportProps {
	onImport: (data: any[], mode: ImportMode) => Promise<any[] | undefined>;
	onExport: () => Promise<any[]>;
	onExportError?: (data: any[]) => any[];
	validHeaderImport?: string[];
	templateData?: any[];
	entityName: string;
	sampleFileName: string;
	sampleErrorFileName?: string;
	dataType?: "bachelorDegree" | "masterDegree" | "language" | "award" | "scholarship";
}

function hasExactHeaders(headerRow: string[], required: string[]) {
	return headerRow.length === required.length && required.every((h) => headerRow.includes(h));
}

function hasValidHeaders(headerRow: string[], required: string[]) {
	return headerRow.every((h) => required.includes(h));
}

export function ImportExportActions({ onImport, onExport, onExportError, validHeaderImport = [], templateData, entityName, sampleFileName, sampleErrorFileName, dataType }: ImportExportProps) {
	const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
	const [isImporting, setIsImporting] = useState(false);
	const [importProgress, setImportProgress] = useState(0);
	const [importResults, setImportResults] = useState<{
		success: number;
		errors: string[];
	} | null>(null);
	const [fileNameSelected, setFileNameSelected] = useState("");
	const importFileRef = useRef<File | null>(null);

	const handleExport = async (exportData: any[], isError: boolean = false) => {
		try {
			const wb = XLSX.utils.book_new();
			const ws = XLSX.utils.json_to_sheet(exportData);
			XLSX.utils.book_append_sheet(wb, ws, "Sheet 1");
			const colCount = Object.keys(exportData[0]).length;
			ws['!cols'] = Array(colCount).fill({ wch: 30 });


			XLSX.writeFile(wb, `${isError ? sampleErrorFileName : sampleFileName}_${new Date().toISOString().split("T")[0]}.xlsx`);
			notify.success(`Đã xuất ${exportData.length} bản ghi thành công`);
		} catch (error) {
			notify.error("Lỗi khi xuất dữ liệu");
		}
	};


	const handleExportTemplate = async () => {
		// Khởi tạo Workbook
		const workbook = new ExcelJS.Workbook();

		// Tạo sheet "Form"
		const wsForm = workbook.addWorksheet('Form');

		// Đổ dữ liệu từ templateData vào wsForm
		if (templateData && templateData.length > 0) {
			// Tự động tạo header dựa trên các key của object đầu tiên
			const columns = Object.keys(templateData[0]).map(key => ({
				header: key,
				key: key,
				width: 30
			}));
			wsForm.columns = columns;

			// Thêm các dòng dữ liệu
			wsForm.addRows(templateData);

			// Định dạng cột ngày tháng (nếu bạn biết tên cột, ví dụ cột 'dob')
			wsForm.getColumn('CCCD (*)').numFmt = '@';
			wsForm.getColumn('Ngày sinh (*)').numFmt = '@';

			if (dataType === 'bachelorDegree') {
				wsForm.getColumn('Mã sinh viên (*)').numFmt = '@';
			}
			if (dataType === 'scholarship') {
				wsForm.getColumn('Mã sinh viên (*)').numFmt = '@';
			}
			if (dataType === 'masterDegree') {
				wsForm.getColumn('Ngày bảo vệ (*)').numFmt = '@';
				wsForm.getColumn('Mã sinh viên (*)').numFmt = '@';
			}
			if (dataType === 'language') {
				wsForm.getColumn('Khoá học bắt đầu').numFmt = '@';
				wsForm.getColumn('Khoá học kết thúc').numFmt = '@';
			}
		}

		if (dataType === 'language') {
			// Tạo sheet "Data" để chứa danh sách lựa chọn (Selector)
			const wsData = workbook.addWorksheet('Data');
			wsData.addRows(languageLevelOptions.map(option => [option.value]));
			// Ẩn sheet Data
			wsData.state = 'hidden';
			// Thêm Data Validation (Selector) vào cột K (từ dòng 2 đến 1000)
			for (let i = 2; i <= 1000; i++) {
				wsForm.getCell(`R${i}`).dataValidation = {
					type: 'list',
					allowBlank: true,
					formulae: ['Data!$A$1:$A$3']
				};
			}
		}
		if (dataType === 'scholarship') {
			// Lấy danh sách danh hiệu từ API
			const { data } = await scholarshipTitleApi.getAll();
			const titles = data.data.map(item => item.titleName);
			// Tạo sheet "Title" để chứa danh sách lựa chọn (Selector) danh hiệu
			const wsTitle = workbook.addWorksheet('Title');
			wsTitle.addRows(titles.map(title => [title]));
			wsTitle.state = 'hidden'; // Ẩn sheet Title
			// Thêm Title Validation (Selector) vào cột N (từ dòng 2 đến 1000)
			for (let i = 2; i <= 1000; i++) {
				wsForm.getCell(`J${i}`).dataValidation = {
					type: 'list',
					allowBlank: true,
					formulae: ['Title!$A$1:$A$' + titles.length]
				};
			}
			// Kết thúc thêm Title Validation

			// Tạo sheet "Major" để chứa danh sách lựa chọn (Selector)
			const wsMajor = workbook.addWorksheet('Major');
			wsMajor.addRows(majorListForScholarship.map(major => [major]));
			wsMajor.state = 'hidden'; // Ẩn sheet Major
			// Thêm Major Validation (Selector) vào cột C (từ dòng 2 đến 1000)
			for (let i = 2; i <= 1000; i++) {
				wsForm.getCell(`C${i}`).dataValidation = {
					type: 'list',
					allowBlank: true,
					formulae: ['Major!$A$1:$A$' + majorListForScholarship.length]
				};
			}
			// Kết thúc thêm Major Validation
		}


		// 6. Xuất file
		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
		saveAs(blob, `template_${sampleFileName}.xlsx`);

		notify.success("Đã tải xuống file mẫu");
	};


	const handleFileChange = async () => {
		// @ts-expect-error
		const [handle] = await window.showOpenFilePicker({
			types: [
				{
					description: 'Excel Files',
					accept: {
						'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
						'application/vnd.ms-excel': ['.xls'],
					},
				},
			],
			multiple: false,
		});

		const file = await handle.getFile();
		const fileName = file.name;
		// const extension = fileName.split(".").pop().toLowerCase();
		setFileNameSelected(fileName);


		importFileRef.current = file;
	};

	const clear = () => {
		setImportResults(null);
		setImportProgress(0);
		setFileNameSelected("");
	};

	const importFile = async ({ mode }: { mode: ImportMode }) => {
		try {
			if (importFileRef.current === null) {
				throw new Error("Vui lòng chọn file tại ô chọn file");
			}

			setIsImporting(true);
			setImportProgress(0);
			setImportResults(null);

			const buffer = await importFileRef.current.arrayBuffer();
			const wb = XLSX.read(buffer, { type: "array" });
			const ws = wb.Sheets[wb.SheetNames[0]];
			const data = XLSX.utils.sheet_to_json(ws, {
				raw: false,
				// dateNF: "dd/mm/yyyy",
			});
			const de = XLSX.utils.sheet_to_json(ws, { header: 1 });
			// // Header = dòng đầu tiên
			const headerRow = de[0] as string[];

			// console.log(headers);

			// const headerRow = Object.keys(data[0] as object[]);

			if (!hasExactHeaders(headerRow, validHeaderImport)) {
				throw new Error("File không hợp lệ, vui lòng tải lại file mẫu và nhập lại");
			}

			// Simulate progress
			for (let i = 0; i <= 100; i += 10) {
				setImportProgress(i);
				await new Promise((resolve) => setTimeout(resolve, 50));
			}

			const errors = await onImport(data, mode);
			console.log(errors);


			if (errors === undefined) {
				setImportResults({ success: data.length, errors: [] });
				notify.success(`Đã nhập thành công ${data.length} bản ghi`);

				// Close dialog after success
				setTimeout(() => {
					clear();
					setIsImportDialogOpen(false);
				}, 2000);
			} else {
				setImportResults({ success: 0, errors: ["Import không thành công, kiểm tra file báo lỗi"] });
				notify.error(`Nhập không thành công`);

				if (onExportError) {
					handleExport(onExportError(errors.map((x) => ({ ...x, errors: x.errors.join(", ") }))), true);
				}
			}
		} catch (error: any) {
			console.error(error.message);
			let errorMessage = error.message || "Lỗi không xác định";

			if (error.message === 'A requested file or directory could not be found at the time an operation was processed.') {
				errorMessage = "Không tìm thấy file hoặc file đã bị xóa. Vui lòng thử lại."
			}

			if (error.message === 'The requested file could not be read, typically due to permission problems that have occurred after a reference to a file was acquired.') {
				errorMessage = "File đã bị sửa. Hãy chọn lại file."
			}
			setImportResults({ success: 0, errors: [errorMessage] });
			notify.error("Lỗi khi nhập dữ liệu");
		} finally {
			// importFileRef.current = null;
			setIsImporting(false);
		}
	};

	return (
		<div className='flex gap-2'>
			<Dialog
				open={isImportDialogOpen}
				onOpenChange={(value) => {
					setIsImportDialogOpen(value);
					clear();
				}}
			>
				<DialogTrigger asChild>
					<Button variant='outline'>
						<Download className='w-4 h-4' />
						Nhập Excel
					</Button>
				</DialogTrigger>
				<DialogContent className='max-w-xl p-0 gap-0'>
					<DialogHeader className='px-4 pt-4'>
						<DialogTitle>Nhập dữ liệu {entityName}</DialogTitle>
						{/* <DialogDescription>Tải lên file Excel để nhập dữ liệu hàng loạt</DialogDescription> */}
					</DialogHeader>

					<div className='space-y-0'>
						{templateData && (
							<div className='p-4'>
								<div className='p-3 bg-blue-50 rounded-lg'>
									<div className='flex items-center gap-2 mb-2'>
										<FileSpreadsheet className='w-4 h-4 text-blue-600' />
										<span className='text-lg font-medium text-blue-900'>File mẫu</span>
									</div>
									<p className='text-sm text-blue-700 mb-2'>Tải xuống file mẫu để xem định dạng dữ liệu chuẩn</p>
									<Button variant='outline' size='sm' onClick={handleExportTemplate}>
										<Download className='w-3 h-3 mr-1' />
										Tải file mẫu
									</Button>
								</div>
							</div>
						)}

						<hr />

						<div className='p-4 space-y-4'>
							<div className='space-y-2'>
								<Label htmlFor='xlsxFile'>Chọn file Import</Label>
								{/* <Input id='xlsxFile' type='file' accept='.xlsx, .xls' onChange={handleFileChange} disabled={isImporting} /> */}
								<div onClick={() => { handleFileChange() }} className="border cursor-pointer rounded-md h-9 flex gap-3 items-center px-4 text-sm">
									<span className="font-semibold">Choose File</span>
									<span>{fileNameSelected}</span>
								</div>
								<p className='text-xs text-muted-foreground'>Chỉ hỗ trợ file .xls, .xlsx (Excel format)</p>
							</div>

							<div className='flex justify-between'>
								<div className='flex gap-2'>
									<Button
										variant='add'
										size='sm'
										onClick={() => {
											importFile({ mode: "onlyAdd" });
										}}
									>
										Thêm mới
									</Button>
									{/* <Button
										variant='add'
										size='sm'
										onClick={() => {
											importFile({ mode: "addAndCreate" });
										}}
									>
										Thêm mới và cập nhật
									</Button> */}
								</div>
								<Button
									variant='destructive'
									size='sm'
									onClick={() => {
										setIsImportDialogOpen(false);
										clear();
									}}
								>
									Hủy
								</Button>
							</div>

							{isImporting && (
								<div className='space-y-2'>
									<div className='flex items-center gap-2'>
										<Upload className='w-4 h-4 animate-pulse' />
										<span className='text-sm'>Đang xử lý...</span>
									</div>
									<Progress value={importProgress} className='h-2' />
								</div>
							)}

							{importResults && (
								<Alert className={importResults.errors.length > 0 ? "border-red-200 bg-red-50" : "border-green-200 bg-green-50"}>
									<div className='flex items-center gap-2'>
										{importResults.errors.length > 0 ? <AlertCircle className='w-4 h-4 text-red-600' /> : <CheckCircle className='w-4 h-4 text-green-600' />}
										<AlertDescription>
											{importResults.errors.length > 0 ? (
												<div>
													<p className='font-medium text-red-900'>Có lỗi xảy ra:</p>
													<ul className='mt-1 text-sm text-red-700'>
														{importResults.errors.map((error, index) => (
															<li key={index}>• {error}</li>
														))}
													</ul>
												</div>
											) : (
												<p className='text-green-900'>Đã nhập thành công {importResults.success} bản ghi</p>
											)}
										</AlertDescription>
									</div>
								</Alert>
							)}
						</div>
					</div>
				</DialogContent>
			</Dialog>
			<Button
				variant='outline'
				onClick={async () => {
					handleExport(await onExport());
				}}
			>
				<Upload className='w-4 h-4' />
				Xuất Excel
			</Button>
		</div>
	);
}

// Template data generators for each entity type
export const generateBachelorDegreeTemplate = () => [
	{
		"Mã Ngành (*)": "",
		"Ngành (*)": "",
		"CCCD (*)": "",
		"Mã sinh viên (*)": "",
		"Họ và tên (*)": "",
		"Ngày sinh (*)": "",
		"Nơi sinh": "",
		"Giới tính (*)": "",
		"Mã Dân tộc": "",
		"Dân tộc": "",
		"Mã quốc tịch": "",
		"Quốc tịch": "",
		"Năm tốt nghiệp (*)": "",
		"Xếp loại tốt nghiệp": "",
		"Xếp loại tốt nghiệp ID (*)": "",
		"Hình thức đào tạo (*)": "",
		"Số hiệu bằng (cơ sở) (*)": "",
		"Số vào sổ cấp bằng (*)": "",
		"Người ký (*)": "",
		"Chức danh người ký (*)": "",
		"Ngày ký (*)": "",
		"Khoá học (*)": "",
		"Quyết định công nhận số (*)": "",
		"Loại yêu cầu ID (*)": "",
		"Loại yêu cầu (*)": "",
		"Ghi chú": "",
	},
];

export const generateMasterDegreeTemplate = () => [
	{
		"Mã Ngành (*)": "",
		"Ngành (*)": "",
		"CCCD (*)": "",
		"Mã sinh viên (*)": "",
		"Họ và tên (*)": "",
		"Ngày sinh (*)": "",
		"Nơi sinh": "",
		"Giới tính (*)": "",
		"Mã Dân tộc": "",
		"Dân tộc": "",
		"Mã quốc tịch": "",
		"Quốc tịch": "",
		"Năm tốt nghiệp (*)": "",
		"Hình thức đào tạo (*)": "",
		"Số hiệu bằng (cơ sở) (*)": "",
		"Số vào sổ cấp bằng (*)": "",
		"Người ký (*)": "",
		"Chức danh người ký (*)": "",
		"Ngày ký (*)": "",
		"Khoá học (*)": "",
		"Ngày bảo vệ (*)": "",
		"Quyết định công nhận số (*)": "",
		"Loại yêu cầu ID (*)": "",
		"Loại yêu cầu (*)": "",
		"Ghi chú": "",
	},
];

export const generateLanguageCertificateTemplate = () => [
	{
		"CCCD (*)": "",
		"Số báo danh (*)": "",
		"Họ và tên (*)": "",
		"Ngày sinh (*)": "",
		"Nơi sinh": "",
		"Giới tính (*)": "",
		"Mã Dân tộc": "",
		"Dân tộc": "",
		"Mã quốc tịch": "",
		"Quốc tịch": "",
		"Số hiệu bằng (cơ sở) (*)": "",
		"Số vào sổ cấp bằng (*)": "",
		"Người ký (*)": "",
		"Chức danh người ký (*)": "",
		"Ngày ký (*)": "",
		"Khóa học (*)": "",
		"Ngoại ngữ (*)": "",
		"Trình độ ngoại ngữ (*)": "",
		"Khoá học bắt đầu": "",
		"Khoá học kết thúc": "",
		"Hội đồng thi (*)": "",
		"Quyết định thành lập hội đồng (*)": "",
		"Quyết định công nhận số (*)": "",
		"Loại yêu cầu ID (*)": "",
		"Loại yêu cầu (*)": "",
		"Ghi chú": "",
	},
];

export const generateAwardTemplate = () => [
	{
		recipientName: "Lê Minh Cường",
		cccd: "123456789014",
		studentId: "HS2023001",
		awardNumber: "KT2023003",
		awardName: "Giấy khen Học sinh Giỏi",
		awardType: "academic",
		level: "school",
		academicYear: "2022-2023",
		achievementDetails: "Đạt điểm trung bình 9.2/10 trong năm học",
		issuedDate: "2023-05-10",
		institution: "Trường THPT Chu Văn An",
		requestType: "Cấp khen thưởng gốc",
		status: "valid",
		issuerSignature: "Hiệu trưởng Nguyễn Văn Nam",
		description: "Học sinh xuất sắc trong năm học 2022-2023",
	},
];

export const generateScholarshipTemplate = () => [
	{
		"Năm học (*)": "",
		"Học kỳ": "",
		"Ngành (*)": "",
		"Mã sinh viên (*)": "",
		"Họ và tên (*)": "",
		"Ngày sinh (*)": "",
		"CCCD (*)": "",
		// "Điểm TB": "",
		// "Điểm RL": "",
		"Mã KT-CN": "",
		"Tên KT-CN (*)": "",
		"Danh hiệu (*)": "",
		// "Số tiền KT-CN": "",
		"Số quyết định (*)": "",
		// "Mức PT": "",
		"Ghi chú": "",
	},
];
